import { buildAndFilter, buildOrFilter, groupRows } from '~/types';
import { textValueOf, type EditableFilterRow } from '~/types/filters';
import type {
  SellerComparisonFiltersInput,
  StringOperatorInput,
} from '~/graphql/graphql';

export type ComparisonFilterConnector = 'and' | 'or';

export type ComparisonFilterRow = EditableFilterRow;

/**
 * Rows are removed from the middle of the list, so React needs an identity that
 * survives the reindex. A random id rather than a counter, because a saved view
 * rehydrates rows a later counter would collide with.
 */
export function newComparisonFilterRowId(): string {
  return crypto.randomUUID();
}

function namesAnyValue(value: string): boolean {
  return value.replace(/[\s,]/g, '') !== '';
}

export const COMPARISON_FILTER_FIELD = {
  sellerName: 'sellerName',
} as const;

export type ComparisonFilterField =
  (typeof COMPARISON_FILTER_FIELD)[keyof typeof COMPARISON_FILTER_FIELD];

const TEXT_FIELDS = [COMPARISON_FILTER_FIELD.sellerName] as const;

const TEXT_OPERATORS = ['contains', 'eq', 'neq', 'notContain'] as const;

function isOneOf<T extends string>(
  value: string,
  values: readonly T[]
): value is T {
  return (values as readonly string[]).includes(value);
}

export function operatorsForField(field: string): readonly string[] | null {
  if (isOneOf(field, TEXT_FIELDS)) return TEXT_OPERATORS;
  return null;
}

function textClause(
  field: string,
  operator: string,
  part: string
): SellerComparisonFiltersInput {
  const input: StringOperatorInput = { [operator]: part };
  return { [field]: input } as SellerComparisonFiltersInput;
}

export function rowToClause(
  row: ComparisonFilterRow
): SellerComparisonFiltersInput | null {
  const value = textValueOf(row).trim();
  if (value === '') {
    return null;
  }
  if (!isOneOf(row.field, TEXT_FIELDS)) {
    return null;
  }
  if (!isOneOf(row.operator, TEXT_OPERATORS)) {
    return null;
  }
  if (!namesAnyValue(value)) {
    return null;
  }
  return textClause(row.field, row.operator, value);
}

export type ActiveFilterRow = {
  row: ComparisonFilterRow;
  clause: SellerComparisonFiltersInput;
};

export function activeFilterGroups(
  rows: ComparisonFilterRow[]
): ActiveFilterRow[][] {
  const groups: ActiveFilterRow[][] = [];
  for (const group of groupRows(rows)) {
    const active: ActiveFilterRow[] = [];
    for (const row of group) {
      const clause = rowToClause(row);
      if (clause !== null) {
        active.push({ row, clause });
      }
    }
    if (active.length > 0) {
      groups.push(active);
    }
  }
  return groups;
}

export function buildComparisonFiltersInput(
  rows: ComparisonFilterRow[]
): SellerComparisonFiltersInput {
  const groups = activeFilterGroups(rows);

  if (groups.length === 0) {
    return {};
  }

  const groupInputs = groups.map((group) => {
    const clauses = group.map((active) => active.clause);
    return clauses.length === 1
      ? clauses[0]
      : (buildAndFilter(clauses) as SellerComparisonFiltersInput);
  });

  return groupInputs.length === 1
    ? groupInputs[0]
    : (buildOrFilter(groupInputs) as SellerComparisonFiltersInput);
}

export function activeRowCount(rows: ComparisonFilterRow[]): number {
  return activeFilterGroups(rows).flat().length;
}

export function comparisonViewChanged(
  previousActiveViewId: string | null,
  currentActiveViewId: string | null
): boolean {
  return previousActiveViewId !== currentActiveViewId;
}
